import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Send, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUSES = ["all", "draft", "approved", "sent", "paid"];

const fmt = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

const pill = (s) => {
  const map = {
    draft: { bg: "#FEF3C7", text: "#92400E" },
    approved: { bg: "#DBEAFE", text: "#1E40AF" },
    sent: { bg: "#E0E7FF", text: "#181854" },
    paid: { bg: "#DCFCE7", text: "#166534" },
  };
  const c = map[s] || { bg: "#E4E6EF", text: "#6A6E85" };
  return (
    <span className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider" style={{ backgroundColor: c.bg, color: c.text }}>{s}</span>
  );
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    const params = filter === "all" ? {} : { status: filter };
    setLoaded(false);
    api.get("/invoices", { params })
      .then((r) => setInvoices(Array.isArray(r.data) ? r.data : []))
      .finally(() => setLoaded(true));
  };

  useEffect(load, [filter]);

  const setStatus = async (inv, status) => {
    try {
      await api.patch(`/invoices/${inv.id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Could not update invoice"); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-bold text-[#181854] tracking-tight">Invoices</h1>
        <p className="text-[#6A6E85] mt-1">Review draft invoices generated from completed jobs.</p>
      </div>

      <div className="bg-white border border-[#E4E6EF] rounded-lg p-2 inline-flex flex-wrap gap-1 shadow-sm">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`inv-filter-${s}`}
            className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors duration-200 ${filter === s ? "bg-[#181854] text-white" : "text-[#181854] hover:bg-[#ECEDF6]"}`}>{s}</button>
        ))}
      </div>

      <div className="space-y-3">
        {!loaded && (
          <div className="text-center py-12 text-[#6A6E85] bg-white rounded-xl border border-[#E4E6EF]">Loading invoices…</div>
        )}
        {loaded && invoices.map((inv) => (
          <div key={inv.id} data-testid={`inv-card-${inv.id}`} className="bg-white rounded-xl border border-[#E4E6EF] p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono text-[#6A6E85]" data-testid={`inv-number-${inv.id}`}>{inv.number || "—"}</span>
                  <span className="text-xs text-[#6A6E85]">· issued {fmt(inv.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <h3 className="text-lg font-semibold text-[#181854]">{inv.client_name}</h3>
                  {pill(inv.status)}
                </div>
                <Link to={`/office/jobs/${inv.job_id}`} className="text-sm text-[#4F9CE0] hover:underline mt-1 inline-block">{inv.job_title} →</Link>
              </div>
              <div className="text-right">
                <div className="text-xs text-[#6A6E85] uppercase tracking-wider font-semibold">Total</div>
                <div className="text-2xl font-bold text-[#14162E]">${inv.amount.toFixed(2)}</div>
              </div>
            </div>

            <ul className="mt-4 divide-y divide-[#E4E6EF] text-sm">
              {inv.line_items.map((li, idx) => (
                <li key={idx} className="flex justify-between py-2">
                  <span className="text-[#14162E]">{li.description}</span>
                  <span className="text-[#6A6E85]">{li.qty} × ${li.unit_price.toFixed(2)} = ${li.amount.toFixed(2)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-4 flex flex-wrap gap-2">
              <Link to={`/office/invoices/${inv.id}/print`} data-testid={`inv-view-${inv.id}`}
                className="inline-flex items-center gap-2 bg-[#ECEDF6] hover:bg-[#E4E6EF] text-[#181854] px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200">
                <FileText size={14} /> View / print
              </Link>
              {inv.status === "draft" && (
                <button onClick={() => setStatus(inv, "approved")} data-testid={`inv-approve-${inv.id}`}
                  className="inline-flex items-center gap-2 bg-[#181854] hover:bg-[#101038] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200">
                  <Check size={14} /> Approve
                </button>
              )}
              {inv.status === "approved" && (
                <button onClick={() => setStatus(inv, "sent")} data-testid={`inv-send-${inv.id}`}
                  className="inline-flex items-center gap-2 bg-[#1F5C91] hover:bg-[#184A76] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200">
                  <Send size={14} /> Mark as sent
                </button>
              )}
              {inv.status === "sent" && (
                <button onClick={() => setStatus(inv, "paid")} data-testid={`inv-paid-${inv.id}`}
                  className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-[#15803D] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200">
                  <CheckCircle2 size={14} /> Mark paid
                </button>
              )}
            </div>
          </div>
        ))}
        {loaded && invoices.length === 0 && (
          <div className="text-center py-12 text-[#6A6E85] bg-white rounded-xl border border-[#E4E6EF]">No invoices in this view.</div>
        )}
      </div>
    </div>
  );
}
